import * as fhirpath from 'fhirpath';
import { get } from 'lodash';
import moment from 'moment';

/** FHIR observation display values that represent a member's delivery / birth date. */
export const POSTPARTUM_DATE_DISPLAY_TYPES = [
  'ACTUAL_BIRTH_DATE',
  'MEMBER_REPORTED_DELIVERY_DATE',
];

const EXPECTED_DUE_DATE_DISPLAY_TYPE = 'EXPECTED_DUE_DATE';

/** FHIR extension URL for lab/maternity observation reading provenance. */
export const LAB_READING_TYPE_URL = 'https://projectwell.io/fhir/identifiers/lab-reading-type';

/** Value used when a care-team member manually enters a maternity observation in Care Betty. */
export const SELF_REPORTED_LAB_READING_TYPE = 'Self-reported';

const MATERNITY_HEALTH_CODE_PATTERN = /maternity[-_]?health/i;

interface FhirCoding {
  code?: string;
  display?: string;
}

interface FhirCodeableConceptCategory {
  coding?: FhirCoding[];
}

function asCodingArray(value: unknown): FhirCoding[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is FhirCoding => typeof item === 'object' && item !== null);
}

function getCategoryCodings(categories: unknown): FhirCoding[] {
  if (!Array.isArray(categories)) {
    return [];
  }
  return categories.flatMap((category: FhirCodeableConceptCategory) => asCodingArray(category?.coding));
}

/** Normalizes maternity observation display text for comparison. */
function normalizeMaternityDisplay(display?: string): string {
  return display?.toUpperCase().replace(/[\s-]+/g, '_') ?? '';
}

function isPostpartumDisplayType(display: string): boolean {
  const normalizedDisplay = normalizeMaternityDisplay(display);
  return POSTPARTUM_DATE_DISPLAY_TYPES.some(
    (type) => normalizeMaternityDisplay(type) === normalizedDisplay
  );
}

/** Returns the FHIR resource from a bundle entry, or the argument itself when already a resource. */
function getObservationResource(entry: unknown): Record<string, unknown> {
  return (get(entry, 'resource') || entry) as Record<string, unknown>;
}

/** Normalizes profile observation bundles to a flat entry array. */
export function getProfileObservationEntries(profileData: unknown): unknown[] {
  const entries = get(profileData, 'entry');
  if (Array.isArray(entries)) {
    return entries;
  }
  if (Array.isArray(profileData)) {
    return profileData;
  }
  return [];
}

/** Parses a maternity observation date string into `YYYY-MM-DD`. */
export function parseMaternityDateValue(valueString?: string): string {
  if (!valueString) {
    return '';
  }
  if (moment(valueString, 'YYYY-MM-DD', true).isValid()) {
    return moment(valueString, 'YYYY-MM-DD').format('YYYY-MM-DD');
  }
  if (moment(valueString, moment.ISO_8601, true).isValid()) {
    // Parse in UTC so a midnight-UTC datetime (e.g. `2026-06-01T00:00:00.000Z`)
    // keeps its calendar date instead of shifting a day in negative-offset zones.
    return moment.utc(valueString, moment.ISO_8601).format('YYYY-MM-DD');
  }
  return '';
}

function getMaternityTypeDisplay(entry: unknown): string {
  const resource = getObservationResource(entry);
  const codeCodings = asCodingArray(get(resource, 'code.coding'));
  const categoryCodings = getCategoryCodings(get(resource, 'category'));

  const knownTypeDisplay = [...codeCodings, ...categoryCodings]
    .map((coding) => normalizeMaternityDisplay(coding.display))
    .find(
      (display) =>
        display === normalizeMaternityDisplay(EXPECTED_DUE_DATE_DISPLAY_TYPE) ||
        isPostpartumDisplayType(display)
    );

  if (knownTypeDisplay) {
    return knownTypeDisplay;
  }

  const maternityCodeDisplay = codeCodings.find((coding) =>
    MATERNITY_HEALTH_CODE_PATTERN.test(coding.code ?? '')
  )?.display;

  return maternityCodeDisplay ? normalizeMaternityDisplay(maternityCodeDisplay) : '';
}

function getObservationDateValue(resource: Record<string, unknown>, allowEffectivePeriodFallback: boolean): string {
  const valueString =
    fhirpath.evaluate(resource, 'valueString')?.toString() || get(resource, 'valueString')?.toString();
  const parsedValueString = parseMaternityDateValue(valueString);
  if (parsedValueString) {
    return parsedValueString;
  }

  const valueDateTime =
    fhirpath.evaluate(resource, 'valueDateTime')?.toString() || get(resource, 'valueDateTime')?.toString();
  const parsedValueDateTime = parseMaternityDateValue(valueDateTime);
  if (parsedValueDateTime) {
    return parsedValueDateTime;
  }

  if (allowEffectivePeriodFallback) {
    const effectiveStart =
      fhirpath.evaluate(resource, 'effectivePeriod.start')?.toString() ||
      get(resource, 'effectivePeriod.start')?.toString();
    return parseMaternityDateValue(effectiveStart);
  }

  return '';
}

/** Resolved maternity dates and postpartum state for the profile counter. */
export interface MaternityCounterDates {
  /** Expected due date from observations. */
  maternityDate: string;
  /** Most recently reported actual birth or member-reported delivery date, if present. */
  birthDate: string;
  /** Whether the counter should display postpartum weeks. */
  isPostpartum: boolean;
  /** Reference date for postpartum week calculation. */
  postpartumReferenceDate: string;
}

function isActiveObservation(resource: Record<string, unknown>): boolean {
  const status = get(resource, 'status')?.toString();
  return status !== 'entered-in-error' && status !== 'cancelled';
}

function isOnOrPastDate(date: string, referenceDate = moment.utc()): boolean {
  return referenceDate.isSameOrAfter(moment.utc(date, 'YYYY-MM-DD'), 'day');
}

/**
 * Self-reported maternity observations are stored for charting but excluded from
 * gestation/postpartum counter calculations, except delivery/birth date types which
 * always drive the counter.
 */
export function isExcludedFromMaternityCounter(entry: unknown): boolean {
  const resource = getObservationResource(entry);
  const extensions: unknown = get(resource, 'extension');
  if (!Array.isArray(extensions)) {
    return false;
  }

  const readingTypeExtension = (extensions as unknown[]).find(
    (extension: unknown) => get(extension, 'url') === LAB_READING_TYPE_URL,
  );
  if (!readingTypeExtension) {
    return false;
  }

  const readingType = (get(readingTypeExtension, 'valueString') as unknown as string | undefined)
    ?.toString()
    .trim();
  if (readingType?.toLowerCase() !== SELF_REPORTED_LAB_READING_TYPE.toLowerCase()) {
    return false;
  }

  const maternityType = getMaternityTypeDisplay(entry);
  if (isPostpartumDisplayType(maternityType)) {
    return false;
  }

  return true;
}

/**
 * Formats a maternity observation label for display.
 *
 * @remarks
 * Self-reported care-team entries prefix `MEMBER REPORTED` for types like
 * `EXPECTED_DUE_DATE`. Delivery/birth types already encode member reporting in
 * their name and are shown unchanged.
 */
export function formatMaternityDisplayLabel(display: string, readingType?: string): string {
  if (readingType?.trim().toLowerCase() !== SELF_REPORTED_LAB_READING_TYPE.toLowerCase()) {
    return display;
  }

  const normalizedDisplay = normalizeMaternityDisplay(display);
  if (POSTPARTUM_DATE_DISPLAY_TYPES.some((type) => normalizeMaternityDisplay(type) === normalizedDisplay)) {
    return display;
  }

  return `MEMBER REPORTED ${display}`;
}

/**
 * Resolves the most recently reported delivery date from actual birth and member-reported
 * delivery observations. Both types are treated with equal priority.
 */
export function getExplicitDeliveryDateFromEntries(entries: unknown[]): string {
  return getMaternityDateFromEntries(entries, POSTPARTUM_DATE_DISPLAY_TYPES, 'newest');
}

/**
 * Resolves the most recently reported delivery date from postpartum observations.
 *
 * @deprecated Prefer {@link getExplicitDeliveryDateFromEntries} for counter postpartum logic.
 */
export function getDeliveryDateFromEntries(entries: unknown[]): string {
  return getExplicitDeliveryDateFromEntries(entries);
}

function matchesMaternityDisplayType(entry: unknown, displayTypes: string[]): boolean {
  const maternityType = getMaternityTypeDisplay(entry);
  const normalizedTypes = displayTypes.map((type) => normalizeMaternityDisplay(type));
  return Boolean(maternityType) && normalizedTypes.includes(maternityType);
}

/**
 * Resolves a maternity counter date from observations.
 *
 * @param displayTypes - One or more `code`/`category` display values to match.
 * @param pick - Whether to use the oldest or newest matching observation record.
 */
export function getMaternityDateFromEntries(
  entries: unknown[],
  displayTypes: string | string[],
  pick: 'oldest' | 'newest' = 'oldest'
): string {
  const normalizedTypes = (Array.isArray(displayTypes) ? displayTypes : [displayTypes]).map((type) =>
    normalizeMaternityDisplay(type)
  );

  const matches = entries.filter((entry) => {
    const resource = getObservationResource(entry);
    if (!isActiveObservation(resource) || isExcludedFromMaternityCounter(entry)) {
      return false;
    }
    const isPostpartumType = normalizedTypes.some((type) => isPostpartumDisplayType(type));
    const valueString = getObservationDateValue(resource, isPostpartumType);
    return matchesMaternityDisplayType(entry, normalizedTypes) && Boolean(valueString);
  });

  matches.sort((a, b) => {
    const aResource = getObservationResource(a);
    const bResource = getObservationResource(b);
    const aRecorded = get(aResource, 'effectivePeriod.start') || get(aResource, 'meta.lastUpdated') || '';
    const bRecorded = get(bResource, 'effectivePeriod.start') || get(bResource, 'meta.lastUpdated') || '';
    const diff = moment(aRecorded).valueOf() - moment(bRecorded).valueOf();
    return pick === 'newest' ? -diff : diff;
  });

  const selected = matches[0];
  if (!selected) {
    return '';
  }

  const selectedResource = getObservationResource(selected);
  const isPostpartumType = normalizedTypes.some((type) => isPostpartumDisplayType(type));
  return getObservationDateValue(selectedResource, isPostpartumType);
}

/** Merges and deduplicates observation entries from multiple FHIR bundles or arrays. */
export function mergeObservationSources(...sources: unknown[]): unknown[] {
  const entries = sources.flatMap((source) => getProfileObservationEntries(source));
  const seen = new Set<string>();

  return entries.filter((entry) => {
    const id = fhirpath.evaluate(getObservationResource(entry), 'id')?.toString();
    if (!id) {
      return true;
    }
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
}

/** Resolves a maternity counter date from a prop or observation entries. */
export function resolveMaternityCounterDate(
  dateProp: string | undefined,
  entries: unknown[],
  displayTypes: string | string[],
  pick: 'oldest' | 'newest' = 'oldest'
): string {
  if (dateProp && moment(dateProp, 'YYYY-MM-DD', true).isValid()) {
    return moment(dateProp, 'YYYY-MM-DD').format('YYYY-MM-DD');
  }
  return getMaternityDateFromEntries(entries, displayTypes, pick);
}

/**
 * Resolves expected due date, delivery date, and postpartum state for the patient banner counter.
 *
 * @remarks
 * **Gestation weeks** use the oldest reported `EXPECTED_DUE_DATE` observation that is not
 * tagged with `lab-reading-type: Self-reported`. Self-reported `ACTUAL_BIRTH_DATE` and
 * `MEMBER_REPORTED_DELIVERY_DATE` observations still drive postpartum week calculations.
 *
 * **Postpartum weeks** use, in order:
 * 1. Most recently reported `ACTUAL_BIRTH_DATE` or `MEMBER_REPORTED_DELIVERY_DATE` (including self-reported)
 * 2. Oldest reported `EXPECTED_DUE_DATE`, once that date is reached, when no delivery date exists (excluding self-reported)
 */
export function resolveMaternityCounterDates(
  maternityDateProp: string | undefined,
  birthDateProp: string | undefined,
  observationSources: unknown[] = []
): MaternityCounterDates {
  const entries = mergeObservationSources(...observationSources);

  const maternityDate = resolveMaternityCounterDate(
    maternityDateProp,
    entries,
    EXPECTED_DUE_DATE_DISPLAY_TYPE,
    'oldest'
  );

  const explicitDeliveryDate =
    (birthDateProp && moment(birthDateProp, 'YYYY-MM-DD', true).isValid()
      ? moment(birthDateProp, 'YYYY-MM-DD').format('YYYY-MM-DD')
      : '') || getExplicitDeliveryDateFromEntries(entries);

  const hasPassedDueDate = Boolean(maternityDate) && isOnOrPastDate(maternityDate);
  const isInferredPostpartum = hasPassedDueDate && !explicitDeliveryDate;
  const isPostpartum = Boolean(explicitDeliveryDate) || isInferredPostpartum;
  const postpartumReferenceDate =
    explicitDeliveryDate || (isInferredPostpartum ? maternityDate : '');

  return {
    maternityDate,
    birthDate: explicitDeliveryDate,
    isPostpartum,
    postpartumReferenceDate,
  };
}
